import { useMemo } from 'react';
import {
  FilterToolbarGroup,
  FilterToolbarItemId,
} from 'src/components/Filters';
import { useContent } from 'src/contexts/ContentContext';
import { PosSelect } from 'src/core/POS/PosSelect';
import { useTagsForEntityType } from 'src/hooks/useTagsForEntityType';
import { useUserHasAdvancedSearchV2Feature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { TAG_OPERATION_TO_CID } from 'src/utils/constants/contentIdMaps';
import { ActionOutboxEntityType } from 'src/WebApiController';

import { TagValueFilterInput } from './TagValueFilterInput';

export const useTagFiltersBase = (
  tagName: string | null,
  tagValue: string | null,
  tagValues: string[] | null,
  tagOperation: string | null,
  setTagName: (tagName: string | null) => void,
  setTagValue: (tagValue: string | null) => void,
  setTagValues: (tagValues: string[]) => void,
  setTagOperation: (op: string | null) => void,
  titleContentId: ContentId,
  entityType:
    | ActionOutboxEntityType.Listing
    | ActionOutboxEntityType.Purchase
    | ActionOutboxEntityType.Sale
    | ActionOutboxEntityType.SellerEvent,
  includeInReport?: boolean,
  disabled?: boolean,
  tagNameFilterId = 'tagName',
  tagValueFilterId = 'tagValue'
): FilterToolbarGroup => {
  const isV2 = useUserHasAdvancedSearchV2Feature(entityType);

  const { tagsMetadata: existingTagsMetadata = [] } = useTagsForEntityType(
    disabled ? undefined : entityType,
    includeInReport
  );

  const noTagsMsg = useContent(ContentId.NoTagsExist);

  const selectedTagMetadata = useMemo(() => {
    return (existingTagsMetadata ?? []).find((r) => r.key === tagName);
  }, [existingTagsMetadata, tagName]);

  const keyOptionsContent = useMemo(() => {
    const keyOptions = (existingTagsMetadata ?? [])
      .toSorted((r1, r2) => r1.key.localeCompare(r2.key))
      .reduce(
        (re, r) => {
          re[r.key] = r.key;
          return re;
        },
        {} as Record<string, string>
      );

    return keyOptions ?? {};
  }, [existingTagsMetadata]);

  const noTagsExist = useMemo(() => {
    return (existingTagsMetadata ?? []).length === 0;
  }, [existingTagsMetadata]);

  return {
    titleContentId,
    type: 'row',
    items: [
      {
        filterId: tagNameFilterId as FilterToolbarItemId,
        labelContentId: ContentId.Name,
        filterQueryKeys: [tagNameFilterId] as FilterToolbarItemId[],
        filterItem: (
          <PosSelect
            style={{ width: '100%' }}
            onChange={(newValue: string) => {
              if (tagName !== newValue) {
                setTagName(newValue);
              }
            }}
            value={tagName ?? ''}
            displayText={noTagsExist ? noTagsMsg : (tagName ?? '')}
            searchable
            disabled={noTagsExist}
            valueOptionsContent={keyOptionsContent}
            placeholderText={ContentId.None}
            enableEmptySelection
            shape="rectFull"
          />
        ),
      },
      ...(isV2
        ? [
            {
              filterId:
                `${tagNameFilterId}Op` as unknown as FilterToolbarItemId,
              labelContentId: ContentId.Operation,
              filterQueryKeys: [
                `${tagNameFilterId}Op`,
              ] as unknown as FilterToolbarItemId[],
              filterItem: (
                <PosSelect
                  style={{ width: '100%' }}
                  onChange={(newValue: string) => {
                    setTagOperation(newValue || null);
                  }}
                  value={
                    tagOperation && tagOperation in TAG_OPERATION_TO_CID
                      ? tagOperation
                      : 'Contains'
                  }
                  valueOptionsContent={TAG_OPERATION_TO_CID}
                  shape="rectFull"
                />
              ),
            },
          ]
        : []),
      {
        filterId: tagValueFilterId as FilterToolbarItemId,
        labelContentId: ContentId.Value,
        filterQueryKeys: [tagValueFilterId] as FilterToolbarItemId[],
        filterItem: (
          <TagValueFilterInput
            valueType={selectedTagMetadata?.valueType}
            disabled={selectedTagMetadata == null || noTagsExist}
            value={tagValue ?? ''}
            values={tagValues ?? undefined}
            onChange={(newValue) => {
              if (tagValue !== newValue) {
                setTagValue(newValue);
              }
            }}
            onValuesChange={setTagValues}
            isMultiValue={isV2}
          />
        ),
      },
    ].filter((f) => f!),
  };
};
